import { Injectable } from '@angular/core';
import { CrudService } from '../service/crud-service';
import { HttpClient, HttpParams } from '@angular/common/http';
import UAParser from 'ua-parser-js';
import { Pageable } from '../service/pageable';
import { HitTracking } from '../hit-tracking-entity';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class HitTrackingService extends CrudService<HitTracking> {
  protected path = '/user-tracking';

  constructor(protected http: HttpClient) {
    super(http);
  }

  protected buildInstance(raw: any): HitTracking {
    return {
      id: raw.id,
      uuid: raw.uuid,
      hit_source: raw.hit_source,
      action: raw.action,
      action_description: raw.action_description,
      is_key_action: raw.is_key_action,
      content_description: raw.content_description,
      hit_time: raw.hit_time
    };
  }

  protected buildPageableInstance(raw: any): Pageable<HitTracking> {
    const content = (raw.content || []).map(item =>
      this.buildInstance(item)
    );
    return new Pageable<HitTracking>(content, raw.totalElements);
  }

  /**
   * ✅ For login: generates metadata with browser + device info
   */
  getLoginMetadata(): any {
    const parser = new UAParser(navigator.userAgent).getResult();

    return {
      deviceType: parser.device.type || 'desktop',
      osName: parser.os.name || '',
      osVersion: parser.os.version || '',
      browserName: parser.browser.name || '',
      browserVersion: parser.browser.version || '',
      engine: parser.engine.name || '',
      engineVersion: parser.engine.version || '',
      cpu: parser.cpu.architecture || '',
      userAgent: navigator.userAgent
    };
  }
  getFirstTimeSetupMetadata(): any {
    const parser = new UAParser(navigator.userAgent).getResult();

    return {
      deviceType: parser.device.type || 'desktop',
      osName: parser.os.name || '',
      osVersion: parser.os.version || '',
    };
  }

  /**
   * ✅ Standard tracking — ensures content_description format is consistent
   */
  hitTheUserTracking(
    hit_source: string,
    returnedData: any,
    action: string,
    action_description: string,
    is_key_action: boolean = true
  ): Observable<HitTracking> {
    const now = new Date().toISOString();

    const content_description = Array.isArray(returnedData)
      ? returnedData.map(item => ({ description: this.stringifyContent(item) }))
      : [{ description: this.stringifyContent(returnedData) }];

    const hit: Partial<HitTracking> = {
      hit_source,
      action,
      action_description: { action_name: action_description },
      is_key_action,
      content_description,
      hit_time: now
    };

    return super.addOne(hit as HitTracking);
  }

  /**
   * 🧠 Safely converts input to string for use in content_description
   */
  private stringifyContent(data: any): string {
    if (typeof data === 'string') {
      return data;
    }

    if (typeof data === 'object') {
      try {
        return JSON.stringify(data);
      } catch {
        return '[Unserializable Object]';
      }
    }

    return String(data);
  }

  listAll(params?: HttpParams) {
    return this.findAll(params);
  }
}
